import { useEffect, useMemo, useState } from 'react';
import { Input, Pagination, Tooltip } from 'antd';
import { ProfileLogo } from '../baseComponents';
import { PlayCircleOutlined } from '@ant-design/icons';

import { useDispatch } from 'react-redux';
import { PastEncounterTopBar } from './PastEncounter.Topnavbar';
import { EncounterTable } from '../baseComponents/Table';
import { CloseAiIcon, EditableIcon, UpdateIcon, PinIcon } from 'src/icons';
import { Activity } from '@insight-health-ai/roseate/icons';
import './PastEncounter.scss';
import { LoadingBar } from '../baseComponents/LoadingBar';
import { useNavigate } from 'react-router-dom';
import {
  encounterDetailsSlice,
  mobileViewStatusSlice,
  setMobileRecord,
} from '../../store/slices/encounter';
import MobileEncounterDetails from './MobileEncounterDetails';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Bars } from 'react-loader-spinner';
import { MicroPhone } from 'src/icons/Microphone.icon';
import {
  formatEncounterStatus,
  getEncounterStatus,
} from '../../utils/encounterHelper';
import { storeInLocal } from '../../../../lib/storage';

import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import { isAllowedToCreateEncounter } from './permissions';
import DeleteEncounter from './DeleteEncounter';
import { EncounterPatient } from './AddPatientModalV2';
import { listEncounters } from '../../store/actions/encounter';
import { analytics } from 'src/services';
import { ehrSubmitDocument } from 'src/modules/EHR/store/slices/ehr';
import { QrCode01 } from '@insight-health-ai/roseate/icons';

const PastEncounterMobileView = ({
  setSearchFilters,
  setStartDate,
  setEndDate,
  topBarInputs,
  setTopBarInputs,
  encounterStatus,
  startDate,
  endDate,
  activeButton,
  setActiveButton,
  handleCreateEncounter,
  isEncounterListLoading,
  selectedEncounter,
  sortState,
  pageState,
  encounterListCount,
  storedParams,
  setPageState,
  searchFilters,
  setSelectedTab,
  selectedTab,
  encounterListWithColors,
  setRecord,
  editingRow,
  handleSaveDescription,
  setEditingRow,
  handleQrCodeOpen,
  setSelectedLastWeek,
  setSelectedTabDate,
  today,
  handleRowClick,
  onCreateEncounter,
  auraDemoOpen,
  onDemoEncounterTrigger,
  setSelectedPractitioner,
  selectedPractitioner,
  schedulepage,
  setEncounterStatus,
  changeEncounterStatus,
  encounterPhase,
  setSelectedDate,
  recentSearchList,
  suggestions,
  isScribeSelected,
  setIsScribeSelected,
  handleEncountersSorting,
  setIsUpgradeModalVisible,
  handleUpgradeToProModal,
  patientDetails,
  settingsOrgValue,
  ehrSettings,
}) => {
  const { encounterList, pinnedEncounterList, isPinnedEncounterListLoading } =
    useSelector((state) => {
      return state?.encounterState;
    });

  // Filter out pinned encounters from the regular list to avoid duplication
  const filteredEncounterList = useMemo(() => {
    if (!pinnedEncounterList?.length) return encounterList;
    const pinnedIds = new Set(
      pinnedEncounterList.map((enc) => enc.encounter_id)
    );
    return (
      encounterList?.filter((enc) => !pinnedIds.has(enc.encounter_id)) || []
    );
  }, [encounterList, pinnedEncounterList]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobileRecord, newEncounterfromMic } = useSelector(
    (state) => state?.encounterState
  );
  const { quota, timeQuota } = useSelector((state) => state?.quotaState);
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { isEntitled: isRetryDemoEntitled } =
    useFeatureEntitlements('retry-demo');

  const { isEntitled: canCreateEncounter } =
    usePermissionEntitlements('encounter.create');

  const isCreateEncounterAllowed = useMemo(() => {
    return isAllowedToCreateEncounter({
      remainingEncounters: quota?.encounters?.remaining,
      remainingDays: timeQuota?.remaining_days,
      canCreateEncounter,
    });
  }, [
    quota?.encounters?.remaining,
    timeQuota?.remaining_days,
    canCreateEncounter,
  ]);

  const { user } = useAuth();

  const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');
  const lastweek = moment().subtract(7, 'days').format('YYYY-MM-DD');

  //Function that happens when a row is clicked in the encounters table
  const handleMobileRowClick = (record) => {
    dispatch(encounterDetailsSlice(null));
    dispatch(ehrSubmitDocument(null));
    handleRowClick(record);
    dispatch(setMobileRecord(true));
    dispatch(mobileViewStatusSlice(record));
    setSelectedTab('Today');
    setSelectedLastWeek(null);
    navigate(`/mobileEncounterDetails/${record?.encounter_id}`);

    setSelectedTabDate(today);
    setPageState({
      current: 1,
      pageSize: 10,
    });
  };

  //Functions to change description of an encounter
  const handleDescriptionChange = (e, record) => {
    const updatedEncounter = { ...record, description: e.target.value };
    setEditingRow(updatedEncounter);
  };

  const [editInputOpen, setEditInputOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedPinnedRowKeys, setSelectedPinnedRowKeys] = useState([]);
  //Table columns
  const columns = [
    {
      title: (
        <div className="flex items-center">
          {encounterList?.length > 0 ? '' : ''}
        </div>
      ),
      dataIndex: 'description',
      key: 'description',
      width: '20%',
      render: (text, record) => {
        const { color, displayStatus } = formatEncounterStatus(record);

        return (
          <div className="flex w-full px-1 py-2 gap-2">
            <ProfileLogo
              name={`${record.practitioner_first_name} ${record.practitioner_last_name}`}
              className={'profile-popover-logo'}
              practitioner_name={record?.practitioner_name}
              practitioner_id={record?.practitioner_id}
            />
            <div className="flex-grow">
              {editingRow?.encounter_id === record.encounter_id ? (
                <div className="flex items-center gap-3 w-full">
                  <Input
                    className="edit-input"
                    style={{
                      width: '100%',
                      background: '#ffffff',
                      border: '1px solid #00d090',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      borderRadius: '5px',
                      padding: '5px 12px',
                    }}
                    defaultValue={editingRow?.description}
                    onChange={(e) => handleDescriptionChange(e, record)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div
                    className="bg-[#00d091] rounded-full flex items-center justify-center p-1"
                    style={{ padding: '4px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveDescription(editingRow, storedParams);
                      setEditInputOpen(false);
                    }}
                  >
                    <UpdateIcon />
                  </div>
                  <div
                    className="bg-[#d63232] rounded-full flex items-center justify-center "
                    style={{ padding: '4px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditInputOpen(false);
                      setEditingRow(null);
                    }}
                  >
                    <CloseAiIcon stroke="#ffffff" fill="#ffffff" className="" />
                  </div>
                </div>
              ) : (
                <div className="text-[13px] font-medium flex gap-3">{text}</div>
              )}

              <div className="text-gray-500 text-[12px] flex flex-col">
                <div className="" style={{ color }}>
                  {displayStatus}
                </div>
                <div className="flex">{record.practitioner_name}</div>
                <div>
                  {moment(record.start_time).format('MMM Do, YYYY')}{' '}
                  {moment(record.start_time).format('h:mm A')}
                </div>
                <EncounterPatient encounter={record} />
              </div>
            </div>
            <div
              className="flex gap-3 items-center justify-end"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Tooltip title="Edit Name" placement="topLeft">
                <span
                  onClick={(e) => {
                    setEditInputOpen(true);
                    setEditingRow(record);
                  }}
                >
                  <EditableIcon />
                </span>
              </Tooltip>
              <Tooltip title="Show QR code" placement="top">
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => {
                    handleQrCodeOpen(record);
                  }}
                >
                  <QrCode01 className="text-lg" />
                </div>
              </Tooltip>
              <Tooltip title="Remove Encounter" placement="topRight">
                <div className="">
                  <DeleteEncounter
                    encounterDetailsSlice={record}
                    searchFilters={searchFilters}
                    record={record}
                    storedParams={storedParams}
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        );
      },
      onCell: (record) => ({
        onClick: (e) => {
          if (
            e.target.closest('.ant-tooltip') ||
            e.target.closest('[data-testid="delete-encounter"]') ||
            editInputOpen
          ) {
            return;
          }
          if (!newEncounterfromMic) {
            setEncounterStatus(getEncounterStatus(record, encounterPhase));
            setRecord(record);
          }
          handleMobileRowClick(record);
        },
      }),
    },
  ];

  //Function to store and reset selected tab dates in mobile view
  const handleTabChange = (key) => {
    storeInLocal(`${currentPractitioner?.org_uuid}_selectedTab`, key);
    setPageState({
      current: 1,
      pageSize: 10,
    });
    setSelectedTab(key);
    if (key === 'Yesterday') {
      setSelectedTabDate(yesterday);
      setSelectedLastWeek(null);
    } else if (key === 'Today') {
      setSelectedTabDate(today);
      setSelectedLastWeek(null);
    } else if (key === 'Past 7 days') {
      setSelectedTabDate(today);
      setSelectedLastWeek(lastweek);
    } else {
      setSelectedTabDate(today);
      setSelectedLastWeek(null);
    }
  };

  const retryDemoJsx =
    isRetryDemoEntitled && encounterListCount < 5 ? (
      <div className="flex justify-center items-center w-full">
        <button
          disabled={isEncounterListLoading || !!auraDemoOpen}
          onClick={() => {
            // eslint-disable-next-line no-undef
            analytics.track('Clicked Retry Demo From Aura', {
              mobile: true,
              email: user?.email,
            });
            onDemoEncounterTrigger();
          }}
          className="ih-button-ghost gap-2 text-[#5a8dda] underline"
        >
          <PlayCircleOutlined />
          Retry Demo
        </button>
      </div>
    ) : null;

  useEffect(() => {
    if (storedParams) {
      dispatch(listEncounters(storedParams, true));
    }
  }, [dispatch, storedParams]);

  return (
    !schedulepage && (
      <>
        <div
          className={`${
            isMobileRecord || schedulepage ? '' : 'page-wrapper'
          } past flex flex-col relative `}
        >
          {/* Date Tabs*/}
          <div className="mb-4">
            {/*Top Bar Mobile view*/}
            <PastEncounterTopBar
              setSearchFilters={setSearchFilters}
              searchFilters={searchFilters}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              topBarInputs={topBarInputs}
              setTopBarInputs={setTopBarInputs}
              encounterStatus={encounterStatus}
              startDate={startDate}
              endDate={endDate}
              activeButton={activeButton}
              setActiveButton={setActiveButton}
              onCreateEncounter={() => handleCreateEncounter(storedParams)}
              selectedEncounterDetails={selectedEncounter}
              encounterList={encounterList}
              encounterListWithColors={encounterListWithColors}
              setPageState={setPageState}
              selectedPractitioner={selectedPractitioner}
              setSelectedPractitioner={setSelectedPractitioner}
              schedulepage={schedulepage}
              changeEncounterStatus={changeEncounterStatus}
              setSelectedDate={setSelectedDate}
              selectedTab={selectedTab}
              handleTabChange={handleTabChange}
              recentSearchList={recentSearchList}
              setSelectedTab={setSelectedTab}
              handleRowClick={handleRowClick}
              isScribeSelected={isScribeSelected}
              setIsScribeSelected={setIsScribeSelected}
              setIsUpgradeModalVisible={setIsUpgradeModalVisible}
              handleUpgradeToProModal={handleUpgradeToProModal}
            />
          </div>

          {/* View when there are no encounters */}
          {!isMobileRecord &&
          encounterList.length === 0 &&
          pinnedEncounterList.length === 0 &&
          startDate === null &&
          endDate === null &&
          searchFilters?.practitioner_id === undefined ? (
            isEncounterListLoading ? (
              <Bars
                height="25"
                width="25"
                color="#00D090"
                ariaLabel="bars-loading"
                wrapperStyle={{}}
                wrapperClass=" justify-center"
                visible={true}
              />
            ) : selectedTab === 'Yesterday' || selectedTab === 'Past 7 days' ? (
              /* Table view when there are no encounters */
              <EncounterTable
                columns={columns}
                setStartDate={setStartDate}
                handleEncountersSorting={handleEncountersSorting}
                setEndDate={setEndDate}
                setSearchFilters={setSearchFilters}
                setTopBarInputs={setTopBarInputs}
                onCreateEncounter={() => handleCreateEncounter(storedParams)}
                ClassName={'tableai'}
                rowClassName={(record) => {
                  let baseClass = '';

                  if (
                    selectedEncounter &&
                    record?.encounter_id === selectedEncounter?.encounter_id
                  ) {
                    baseClass =
                      'bg-gray-100 shadow-inner transform transition duration-200 ease-in-out hover:shadow-lg cursor-pointer overflow-x-hidden';
                  }
                  baseClass = '';

                  if (selectedRowKeys.includes(record?.encounter_id)) {
                    baseClass += ' row-selected';
                  }

                  return baseClass;
                }}
                showSorterTooltip={false}
                dataSource={filteredEncounterList}
                loading={{
                  indicator: <LoadingBar />,
                  spinning: isEncounterListLoading,
                }}
                locale={{
                  emptyText: 'No Data',
                }}
                tableLayout="auto"
                // Sorting
                sortDirections={['asc', 'desc', 'asc']}
                sortedInfo={sortState}
                selectedRowKeys={selectedRowKeys}
                storedParams={storedParams}
                setSelectedRowKeys={setSelectedRowKeys}
              />
            ) : (
              /* Default View when there are no encounters */
              <div
                className="flex justify-center items-center w-full flex-col"
                style={{ minHeight: '75vh' }}
              >
                <div className="bg-[#00D090] rounded-full p-5 flex items-center justify-center mb-7 cursor-pointer">
                  <MicroPhone onClick={onCreateEncounter} />
                </div>
                <div className="mb-5 text-center">
                  Click on the microphone to create a new encounter
                </div>
              </div>
            )
          ) : !isMobileRecord &&
            (encounterList?.length > 0 ||
              pinnedEncounterList?.length > 0 ||
              (encounterList.length === 0 &&
                pinnedEncounterList.length === 0 &&
                (startDate || endDate || searchFilters?.practitioner_id))) ? (
            <>
              {/*Table Mobile view*/}
              <div className="">
                <div className="">
                  <div className="mt-2 mb-24 relative ">
                    <div
                      className={`grid shadow-xl ant-table-wrappers rounded-xl bg-white relative  `}
                    >
                      <div className="relative ">
                        {pinnedEncounterList.length > 0 && (
                          <>
                            <EncounterTable
                              columns={columns}
                              setStartDate={setStartDate}
                              handleEncountersSorting={handleEncountersSorting}
                              setEndDate={setEndDate}
                              setSearchFilters={setSearchFilters}
                              setTopBarInputs={setTopBarInputs}
                              onCreateEncounter={() =>
                                handleCreateEncounter(storedParams)
                              }
                              ClassName={'tableai'}
                              rowClassName={(record, index) => {
                                let baseClass = 'bg-[#E9F5F4]';
                                if (
                                  (selectedEncounter &&
                                    record?.encounter_id ===
                                      selectedEncounter?.encounter_id) ||
                                  (index === 0 &&
                                    (selectedTab === 'Today' ||
                                      selectedTab === 'Yesterday' ||
                                      selectedTab === 'Past 7 days'))
                                ) {
                                  baseClass +=
                                    ' selectedAura bg-[#ebecf0] transform transition duration-200 ease-in-out';
                                }
                                if (
                                  selectedPinnedRowKeys.includes(
                                    record?.encounter_id
                                  )
                                ) {
                                  baseClass += ' row-selected';
                                }
                                return baseClass;
                              }}
                              showSorterTooltip={false}
                              dataSource={pinnedEncounterList}
                              loading={{
                                indicator: <LoadingBar />,
                                spinning: isPinnedEncounterListLoading,
                              }}
                              locale={{
                                emptyText: 'No Data',
                              }}
                              tableLayout="auto"
                              sortDirections={['asc', 'desc', 'asc']}
                              sortedInfo={sortState}
                              selectedRowKeys={selectedPinnedRowKeys}
                              storedParams={storedParams}
                              setSelectedRowKeys={setSelectedPinnedRowKeys}
                              settingsOrgValue={settingsOrgValue}
                              isUrgent={true}
                              title={
                                <div className="font-sans font-medium text-sm flex items-center py-3 pl-4 gap-2">
                                  <PinIcon fill="#1FD0C1" />
                                  Active Encounters
                                  <span className="rounded-full bg-[#E9F5F4] text-[#1FD0C1] min-w-[18px] min-h-[18px] text-xs flex items-center justify-center">
                                    {pinnedEncounterList?.length || 0}
                                  </span>
                                </div>
                              }
                            />
                            <div className="h-px bg-gray-200 my-4"></div>
                          </>
                        )}
                        <EncounterTable
                          columns={columns}
                          setStartDate={setStartDate}
                          handleEncountersSorting={handleEncountersSorting}
                          setEndDate={setEndDate}
                          setSearchFilters={setSearchFilters}
                          setTopBarInputs={setTopBarInputs}
                          onCreateEncounter={() =>
                            handleCreateEncounter(storedParams)
                          }
                          ClassName={'tableai'}
                          rowClassName={(record, index) => {
                            let baseClass = '';
                            if (
                              (selectedEncounter &&
                                record?.encounter_id ===
                                  selectedEncounter?.encounter_id) ||
                              (index === 0 &&
                                (selectedTab === 'Today' ||
                                  selectedTab === 'Yesterday' ||
                                  selectedTab === 'Past 7 days'))
                            ) {
                              baseClass =
                                'bg-gray-100 shadow-inner transform transition duration-200 ease-in-out hover:shadow-lg cursor-pointer overflow-x-hidden';
                            }
                            baseClass = '';
                            if (
                              selectedRowKeys.includes(record?.encounter_id)
                            ) {
                              baseClass += ' row-selected';
                            }
                            return baseClass;
                          }}
                          showSorterTooltip={false}
                          dataSource={filteredEncounterList}
                          loading={{
                            indicator: <LoadingBar />,
                            spinning: isEncounterListLoading,
                          }}
                          locale={{
                            emptyText: 'No Data',
                          }}
                          tableLayout="auto"
                          // Sorting
                          sortDirections={['asc', 'desc', 'asc']}
                          sortedInfo={sortState}
                          selectedRowKeys={selectedRowKeys}
                          storedParams={storedParams}
                          setSelectedRowKeys={setSelectedRowKeys}
                          settingsOrgValue={settingsOrgValue}
                          isUrgent={false}
                          title={
                            pinnedEncounterList.length > 0 ? (
                              <div className="font-sans font-medium text-sm flex items-center pb-1 pl-4 gap-2">
                                <Activity color="#36AEEA" />
                                All Encounters
                              </div>
                            ) : (
                              <></>
                            )
                          }
                        />
                        <div className="sticky bottom-0 bg-white  flex items-center justify-center ">
                          {encounterList.length > 0 && (
                            <Pagination
                              align="center"
                              current={pageState.current}
                              pageSize={pageState.pageSize}
                              total={encounterListCount}
                              onChange={(page, pageSize) => {
                                setPageState({
                                  current: page,
                                  pageSize,
                                });
                              }}
                              className="custom-pagination-class "
                              showSizeChanger={false}
                              pageSizeOptions={['10', '30', '50', '100']}
                              size="small"
                              locale={{
                                items_per_page: '',
                              }}
                            />
                          )}
                        </div>
                        {retryDemoJsx}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : isMobileRecord ? (
            /*Encounter Details in Mobile view*/
            <MobileEncounterDetails
              topBarInputs={topBarInputs}
              storedParams={storedParams}
              schedulepage={schedulepage}
              suggestions={suggestions}
              patientDetails={patientDetails}
            />
          ) : (
            ''
          )}
        </div>
        {/* Floating Mic to create encounter */}
        {!isMobileRecord && isCreateEncounterAllowed && (
          <div className="absolute bottom-16 right-4">
            <div className="bg-[#00D090] rounded-full p-5 flex items-center justify-center mb-7 cursor-pointer">
              <MicroPhone onClick={onCreateEncounter} />
            </div>
          </div>
        )}
      </>
    )
  );
};

export default PastEncounterMobileView;
